import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { DateTimeField } from "../../components/DateTimeField";
import * as Fonts from "../../components/Fonts";
import Modal, { ModalButton, ModalSecondaryButton } from "../../components/Modal";
import CloseIcon from "../../icons/Close";
import { Card } from "../Settings/styles";
import { FieldError, SectionHead } from "./styles";
import { TimePicker } from "./TimePicker";
import { todayIn } from "./timezone";

const prefix = "pages.agenda.settings.blocked";

/** 2026-12-25 → 25/12/2026 */
export const brDate = (date: string) => `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}`;

type Times = Record<string, [string, string]>;

type Props = {
  /** Whole days off. */
  dates: string[];
  /** A part of a day off: one range per day. */
  times: Times;
  /** The national holidays (YYYY-MM-DD => key): a blocked holiday shows its name. */
  holidays: Record<string, string>;
  /** The agenda's: "today" is its. */
  timezone: string;
  disabled: boolean;
  onChange: (dates: string[], times: Times) => void;
};

/**
 * Days off and blocks (holidays, vacations, a morning off): the agenda offers no time in them. The section shows the
 * blocks; adding one opens a modal, the holidays still to come are one tap on the title's right.
 */
export const BlockedDays = ({ dates, times, holidays, timezone, disabled, onChange }: Props) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);

  // the holidays still to come that aren't blocked: one tap blocks them all
  const today = todayIn(timezone);
  const missing = Object.keys(holidays).filter(date => date >= today && !dates.includes(date) && !times[date]);

  // one block per day: blocking a day again takes the place of what it had
  const without = (date: string): [string[], Times] => {
    const rest = Object.fromEntries(Object.entries(times).filter(([key]) => key !== date));
    return [dates.filter(item => item !== date), rest];
  };

  const block = (date: string, range?: [string, string]) => {
    const [otherDates, otherTimes] = without(date);
    if (range) {
      onChange(otherDates, { ...otherTimes, [date]: range });
    } else {
      onChange([...otherDates, date].sort(), otherTimes);
    }
    setAdding(false);
  };

  const blocks = [
    ...dates.map(date => ({ date, range: undefined as [string, string] | undefined })),
    ...Object.entries(times).map(([date, range]) => ({ date, range })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <section className="flex flex-col gap-3">
      {/* the title, and on its right the shortcuts */}
      <SectionHead>
        <Fonts.Label>{t(`${prefix}.title`)}</Fonts.Label>
        {!disabled && (
          <div className="actions">
            {missing.length > 0 && (
              <button
                type="button"
                aria-label={t(`${prefix}.addHolidaysLong`, { count: missing.length })}
                onClick={() => onChange([...dates, ...missing].sort(), times)}>
                {t(`${prefix}.addHolidays`, { count: missing.length })}
              </button>
            )}
            <button type="button" onClick={() => setAdding(true)}>
              {t(`${prefix}.add`)}
            </button>
          </div>
        )}
      </SectionHead>
      <Card>
        <Chips>
          {blocks.length === 0 && <p className="none">{t(`${prefix}.none`)}</p>}
          {/* what the owner added, then the national holidays (whole days of the list) */}
          {[
            { key: "own", items: blocks.filter(item => item.range || !holidays[item.date]) },
            { key: "holidaysGroup", items: blocks.filter(item => !item.range && holidays[item.date]) },
          ]
            .filter(group => group.items.length > 0)
            .map(group => (
              <div key={group.key} className="group">
                <h3>{t(`${prefix}.${group.key}`)}</h3>
                <ul aria-label={t(`${prefix}.${group.key}`)}>
                  {group.items.map(({ date, range }) => {
                    const label = range ? `${brDate(date)} · ${range[0]} – ${range[1]}` : brDate(date);
                    return (
                      <li key={date}>
                        <span>{label}</span>
                        {!range && holidays[date] && (
                          <span className="name">{t(`${prefix}.holidays.${holidays[date]}`)}</span>
                        )}
                        {!disabled && (
                          <button
                            type="button"
                            aria-label={t(`${prefix}.unblock`, { date: label })}
                            onClick={() => onChange(...without(date))}>
                            <CloseIcon width={8} height={8} color="currentColor" />
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
        </Chips>
      </Card>
      {adding && <AddBlock onBlock={block} onExit={() => setAdding(false)} />}
    </section>
  );
};

type AddProps = { onBlock: (date: string, range?: [string, string]) => void; onExit: () => void };

/** The day, and a start and an end when only part of it is off. */
const AddBlock = ({ onBlock, onExit }: AddProps) => {
  const { t } = useTranslation();
  const [date, setDate] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // no start and no end: the whole day; else both, the end after the start
  const wholeDay = !from && !to;
  const badRange = !wholeDay && !!from && !!to && from >= to;
  const canBlock = /^\d{4}-\d{2}-\d{2}$/.test(date) && (wholeDay || (!!from && !!to && from < to));

  return (
    <BlockModal
      title={t(`${prefix}.addTitle`)}
      escapeExit
      onExit={onExit}
      footer={
        <>
          <ModalSecondaryButton type="button" onClick={onExit}>
            {t(`${prefix}.cancel`)}
          </ModalSecondaryButton>
          <ModalButton disabled={!canBlock} onClick={() => onBlock(date, wholeDay ? undefined : [from, to])}>
            {t(`${prefix}.block`)}
          </ModalButton>
        </>
      }>
      <Form>
        <div className="field">
          <label htmlFor="agenda-blocked-day">{t(`${prefix}.pick`)}</label>
          <DateTimeField
            id="agenda-blocked-day"
            kind="DATE"
            label={t(`${prefix}.pick`)}
            placeholder={t(`${prefix}.placeholder`)}
            value={date}
            onChange={setDate}
          />
        </div>
        <div className="times">
          <div className="field">
            <span className="label" aria-hidden="true">
              {t(`${prefix}.from`)}
            </span>
            <TimePicker
              size="field"
              label={t(`${prefix}.from`)}
              placeholder={t(`${prefix}.fromPlaceholder`)}
              value={from}
              onChange={setFrom}
            />
          </div>
          <div className="field">
            <span className="label" aria-hidden="true">
              {t(`${prefix}.to`)}
            </span>
            <TimePicker
              size="field"
              label={t(`${prefix}.to`)}
              placeholder={t(`${prefix}.toPlaceholder`)}
              value={to}
              onChange={setTo}
            />
          </div>
        </div>
        {badRange ? (
          <FieldError role="alert" className="error">
            {t(`${prefix}.invalidRange`)}
          </FieldError>
        ) : (
          <p className="hint">{t(`${prefix}.wholeDayHint`)}</p>
        )}
      </Form>
    </BlockModal>
  );
};

/** A short form: the day's and the times' pickers open over the modal's edges, not cut by them. */
const BlockModal = styled(Modal)`
  .modal-container,
  .modal-body {
    overflow: visible;
  }

  /* the frame no longer cuts the bar's corners: it rounds them itself (none on phones' sheet) */
  footer {
    border-bottom-right-radius: inherit;
    border-bottom-left-radius: inherit;
  }
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: Inter, sans-serif;

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    min-width: 0;
  }

  /* as the editor fields' labels */
  .field label,
  .field .label {
    font-size: 0.6875rem;
    font-weight: 700;
    line-height: 1rem;
    letter-spacing: 0.04em;
    color: var(--color-neutral-150);
    text-transform: uppercase;
  }

  .times {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 0.75rem;
  }

  .hint {
    margin-top: -0.25rem;
    font-size: 0.8125rem;
    color: var(--color-neutral-150);
  }

  .error {
    margin: -0.25rem 0 0;
  }
`;

const Chips = styled.div`
  font-family: Inter, sans-serif;

  .none {
    font-size: 0.8125rem;
    color: var(--color-neutral-150);
  }

  .group + .group {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--color-neutral-base);
  }

  /* as the editor fields' labels */
  h3 {
    margin-bottom: 0.625rem;
    font-size: 0.6875rem;
    font-weight: 700;
    line-height: 1rem;
    letter-spacing: 0.04em;
    color: var(--color-neutral-150);
    text-transform: uppercase;
  }

  ul {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0;
    list-style: none;
  }

  li {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem 0.25rem 0.75rem;
    border-radius: 999px;
    background: var(--color-neutral-base);
    font-size: 0.8125rem;
    color: var(--color-neutral-white);
  }

  li button {
    display: grid;
    place-items: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 999px;
    color: var(--color-neutral-150);
  }

  li button:hover {
    color: var(--color-neutral-white);
  }

  li .name {
    color: var(--color-neutral-150);
  }

  li .name::before {
    content: "·";
    margin-right: 0.25rem;
  }
`;
